import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { accessibleTicketGroupColor } from '@tix/themes/helpers';
import { indexItemsById } from '@/helpers/IndexHelpers';
import { filterQuantitiesByGroups } from '@/helpers/TicketTypeQuantities';
import { reportFormValidity } from '@/helpers/Validation';
import MobileFooterPortal from '@/components/cart/MobileFooterPortal.vue';
import MobileCartButton from '@/components/elements/MobileCartButton.vue';
import MobileTotal from '@/components/elements/MobileTotal.vue';
import SeatMap from '@/components/events/SeatMap.vue';
import TicketGroupSeatBadge from '@/components/events/TicketGroupSeatBadge.vue';
import FormValidationGuard from '@/components/forms/FormValidationGuard.vue';
import { API } from '@/api/API';
import ValidationError from '@/errors/ValidationError';
import { apiErrorMessageOrRethrow } from '@/errors/helpers';
import { ttToTGQuantities } from '@/seats/assignments';
import { getSelectedSeatsMessage, seatSelectionIsComplete } from '@/seats/helpers';
import { getTicketGroupColor } from '../../helpers/TicketGroupsColors';
@Component({
    name: 'SelectSeats',
    components: {
        FormValidationGuard,
        MobileTotal,
        MobileCartButton,
        SeatMap,
        MobileFooterPortal,
        TicketGroupSeatBadge,
    },
})
export default class default_1 extends Vue {
    @Prop({ required: true })
    session;
    @Prop({ required: true })
    selectedGroups;
    @Prop({ required: true })
    quantities;
    @Prop({ required: true })
    value;
    state = 'closed';
    isMobile = true;
    map = null;
    groupFilter = null;
    seatData;
    validationFailed = false;
    created() {
        return API.getCached(`events/${this.session.event_template_id}/sessions/${this.session.id}/seat_map`, {
            query: { _withgeometry: true },
        })
            .then((response) => {
            this.map = response.seat_map._data[0].map_geometry;
            this.seatData = response.seat._data;
        })
            .catch(apiErrorMessageOrRethrow);
    }
    @Watch('value')
    valueChange() {
        if (this.value) {
            this.validationFailed = false;
        }
    }
    get internal() {
        return this.value;
    }
    set internal(selectedSeats) {
        if (selectedSeats) {
            // All seats selected, close the map
            this.state = 'closed';
        }
        this.$emit('input', selectedSeats);
    }
    @Watch('state')
    emitState(state) {
        this.$emit('seatMapOpen', state === 'open');
        this.$nextTick(() => {
            const el = this.$refs.selectedSeats;
            if (el && state === 'closed') {
                el.scrollIntoView();
            }
        });
        // Disable scrolling the page when the mobile modal is open
        const body = document.querySelector('body');
        body.style.overflow = state === 'open' && this.isMobile ? 'hidden' : 'auto';
    }
    beforeDestroy() {
        // Remove all assigned seats before destroy
        this.$emit('input', []);
    }
    applyFilter(groupId) {
        this.groupFilter = this.groupFilter === groupId ? null : groupId;
    }
    get selectMessage() {
        return getSelectedSeatsMessage(this.selectedGroups, this.seatQuantities);
    }
    get seatQuantities() {
        return filterQuantitiesByGroups(this.quantities, this.selectedGroups);
    }
    openMap(isMobile) {
        // Slow devices take a long time to create all the DOM elements for large seat maps. Add an intermediate state
        // while elements are being created to show a spinner. The rendering state is not visible if the seat map has few
        // seats and/or the device is fast (like MacBook Pros), because they can do all of that processing in a single
        // animation frame.
        this.state = 'rendering';
        setTimeout(() => {
            this.state = 'open';
            this.isMobile = isMobile;
        }, 50);
    }
    get groupsById() {
        return indexItemsById(this.selectedGroups);
    }
    /**
     * TODO Remove this once `tg_seat_accessibility` flag is removed.
     */
    getGroupColor(groupId) {
        return `ticket-group-${this.groupsById[groupId].color}-color`;
    }
    get sortedSeats() {
        // Use natural sort (numeric) so that e.g. seats 1-10 are not sorted 1,10,2,3,4...
        return this.value.sort((a, b) => a.seat.name.localeCompare(b.seat.name, undefined, { numeric: true }));
    }
    get isRendering() {
        return this.state === 'rendering';
    }
    get showFindSeatsButton() {
        if (!this.value) {
            return true;
        }
        else {
            const quantities = ttToTGQuantities(this.seatQuantities, this.selectedGroups);
            return !seatSelectionIsComplete(quantities, this.value);
        }
    }
    get showCart() {
        return this.$store.getters['Cart/ticketCount'] > 0;
    }
    validateSeatMap() {
        reportFormValidity(this.$refs.seatSelector).catch((error) => {
            // Error is handled by <FormValidationGuard>
            if (error instanceof ValidationError)
                return;
            throw error;
        });
    }
    get findSeatValidationMsg() {
        return `Please click the '${this.t.findSeats}' button to continue.`;
    }
    handleSeatMapInvalidity(validity) {
        this.validationFailed = validity;
    }
    changeSeatSelection() {
        this.state = 'closed';
        this.validationFailed = false;
    }
    get changeTicketsButton() {
        return { label: 'Change Tickets', onClick: this.changeSeatSelection };
    }
    getGroupColorClass(group) {
        return group.isAccessible
            ? `ticket-group-accessible-color`
            : `ticket-group-${this.groupsById[group.id].color}-color`;
    }
    get styles() {
        const style = {
            '--tg-color-accessible': accessibleTicketGroupColor,
        };
        for (const group of this.selectedGroups) {
            if (group.color) {
                style[`--tg-color-${group.color}`] = getTicketGroupColor(group);
            }
        }
        return style;
    }
}
